import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { HelpCircle, ChevronDown, ChevronUp, Phone, Mail, Clock, Award, Wrench, Users, Globe } from 'lucide-react';

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const toggleQuestion = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "¿Qué servicios ofrecen?",
      answer: "Ofrecemos una gama completa de servicios industriales incluyendo mantenimiento industrial, instalación y montaje, consultoría técnica, repuestos y accesorios, inspección y certificación, y capacitación técnica.",
      icon: Wrench
    },
    {
      question: "¿Qué industrias atienden?",
      answer: "Trabajamos con industria papelera, pesquera, alimentaria, agroindustria y manufactura en general. Contamos con experiencia especializada en cada sector.",
      icon: Globe
    },
    {
      question: "¿Ofrecen garantía?",
      answer: "Sí, todos nuestros productos incluyen garantía del fabricante y nuestros servicios cuentan con soporte post-venta. La garantía varía según el tipo de producto o servicio.",
      icon: Award
    },
    {
      question: "¿Cuál es el tiempo de entrega?",
      answer: "Para productos en stock, el tiempo de entrega es de 24-48 horas. Para productos especiales, el tiempo varía según disponibilidad y puede ser de 3-15 días hábiles.",
      icon: Clock
    },
    {
      question: "¿Atienden emergencias?",
      answer: "Sí, contamos con servicio de emergencias 24/7. Puede contactarnos al +51953759419 para asistencia inmediata.",
      icon: Phone
    },
    {
      question: "¿Cuéntan con certificaciones?",
      answer: "Sí, contamos con certificaciones ISO 9001 y otras certificaciones específicas de cada industria. Nuestro personal está debidamente capacitado y certificado.",
      icon: Award
    },
    {
      question: "¿Dónde están ubicados?",
      answer: "Nuestra oficina principal está en Av. Industrial 1234, Lima, Perú, con presencia en las principales ciudades industriales del país y capacidad de atención nacional.",
      icon: Globe
    },
    {
      question: "¿Cómo solicito una cotización?",
      answer: "Puede solicitar una cotización a través de nuestro formulario web, llamando al +51953759419, o enviando un email a ventas@atomcorporations.com. Responderemos en menos de 24 horas.",
      icon: Mail
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="section-padding">
        <div className="container-custom max-w-4xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-6">
              <HelpCircle className="w-4 h-4 mr-2" />
              Preguntas Frecuentes
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              ¿Tienes
              <span className="text-gradient"> Preguntas</span>?
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Encuentra respuestas rápidas a las consultas más comunes 
              sobre nuestros servicios y soluciones industriales.
            </p>
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-12">
            <div className="space-y-4">
              {faqs.map((faq, index) => {
                const Icon = faq.icon;
                return (
                  <div 
                    key={index} 
                    className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <button
                      onClick={() => toggleQuestion(index)}
                      className="w-full px-6 py-4 bg-white hover:bg-gray-50 transition-colors flex items-center justify-between text-left"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-primary-600" />
                        </div>
                        <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                      </div>
                      <div className="flex-shrink-0">
                        {activeIndex === index ? (
                          <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                      </div>
                    </button>
                    {activeIndex === index && (
                      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Contact Section */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Phone className="w-6 h-6 mr-3 text-primary-600" />
                ¿No encontraste tu respuesta?
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Nuestro equipo de expertos está disponible para responder 
                todas tus preguntas y proporcionarte la información que necesitas.
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Llámanos</div>
                    <div className="text-gray-600">+51953759419</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-accent-600 rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Escríbenos</div>
                    <div className="text-gray-600">ventas@atomcorporations.com</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-secondary-50 to-accent-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Clock className="w-6 h-6 mr-3 text-secondary-600" />
                Horario de Atención
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="font-semibold text-gray-900">Lunes - Viernes</span>
                  <span className="text-gray-700">8:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="font-semibold text-gray-900">Sábados</span>
                  <span className="text-gray-700">9:00 AM - 1:00 PM</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="font-semibold text-gray-900">Emergencias</span>
                  <span className="text-primary-600 font-semibold">24/7</span>
                </div>
              </div>
              <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
                <p className="text-yellow-800 text-sm">
                  <strong>Nota:</strong> Para emergencias fuera del horario comercial, 
                  contacte al +51 953 759 419
                </p>
              </div>
            </div>
          </div>

          {/* Services Quick Links */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Explora Nuestros
              <span className="text-gradient"> Servicios</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors">
                <div className="w-16 h-16 bg-blue-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Wrench className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Mantenimiento</h3>
                <p className="text-gray-600 text-sm mb-4">Servicio completo de mantenimiento preventivo y correctivo</p>
                <a 
                  href="/services" 
                  className="text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                >
                  Ver más →
                </a>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-xl hover:bg-green-100 transition-colors">
                <div className="w-16 h-16 bg-green-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Instalación</h3>
                <p className="text-gray-600 text-sm mb-4">Montaje profesional de equipos industriales</p>
                <a 
                  href="/services" 
                  className="text-green-600 font-semibold hover:text-green-700 transition-colors"
                >
                  Ver más →
                </a>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors">
                <div className="w-16 h-16 bg-purple-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Consultoría</h3>
                <p className="text-gray-600 text-sm mb-4">Asesoría especializada para optimización</p>
                <a 
                  href="/services" 
                  className="text-purple-600 font-semibold hover:text-purple-700 transition-colors"
                >
                  Ver más →
                </a>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-primary-600 to-accent-500 rounded-2xl p-8 md:p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">
              ¿Listo para comenzar tu proyecto?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Contáctanos hoy mismo y recibe una cotización personalizada 
              para tus necesidades industriales.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/quote" 
                className="inline-flex items-center bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Solicitar Cotización
              </a>
              <a 
                href="/contact" 
                className="inline-flex items-center border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors"
              >
                Contactar Asesor
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FAQ;